import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { useStepperContext } from "~/components/ui/stepper";
import { cn } from "~/lib/utils";
import { useIngestionContext } from "./context/IngestionContext";
import { ValidationMetadata, ValidationState } from "./types";
import { stripS3Prefix } from "./utils/jobParsers";

interface ValidationNavigationProps {
  onClose: () => void;
  onFileValidated: (file: FileInfo, validationMetadata: ValidationMetadata) => void;
  /** Called on the success path (Confirm & Submit) to set validated=true in URL */
  onConfirm?: () => void;
}

/** Handles step navigation, ingestion creation, and action buttons */
export const ValidationNavigation = ({
  onClose,
  onFileValidated,
  onConfirm,
}: ValidationNavigationProps) => {
  const { currentStep, setCurrentStep, stepperState } = useStepperContext();
  const {
    selectedFile,
    pendingLocalFile,
    hasIngestion,
    canStartValidation,
    isLoading,
    isUploading,
    error,
    startIngestion,
    uploadPendingFile,
    clearError,
  } = useIngestionContext();

  const isProfiling = stepperState?.isProfiling as boolean | undefined;
  const dataProfile = stepperState?.dataProfile;
  const validationState = stepperState?.validationState as ValidationState | undefined;
  const ingestion = stepperState?.ingestion;
  const recoveredSourceFile = stepperState?.recoveredSourceFile as string | null;

  const isProfilingComplete = !isProfiling && !!dataProfile;
  const originalSourceFile = ingestion?.source_file || recoveredSourceFile;

  /** Check if user selected a different file than the existing ingestion */
  const isSelectingDifferentFile = (): boolean => {
    if (!hasIngestion || !selectedFile || !originalSourceFile) {
      return false;
    }
    return stripS3Prefix(originalSourceFile) !== stripS3Prefix(selectedFile.key);
  };

  const handleNext = async () => {
    clearError();

    if (currentStep === 0) {
      if (hasIngestion && !isStartingFresh) {
        // Resume existing ingestion
        setCurrentStep(1);
      } else if (pendingLocalFile) {
        // Upload flow: create ingestion, move to step 2, upload in background
        const newIngestionId = await startIngestion();
        if (newIngestionId) {
          setCurrentStep(1);
          uploadPendingFile(); // Non-blocking
        }
      } else if (selectedFile) {
        // Cloud file: create ingestion and proceed
        const newIngestionId = await startIngestion();
        if (newIngestionId) {
          setCurrentStep(1);
        }
      }
    } else if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validation failed — send user back to file selection to try again
      if (validationState?.hasErrors) {
        setCurrentStep(0);
        return;
      }

      // Use selectedFile if available, otherwise construct a minimal FileInfo
      // from the recovered source file (page refresh / re-confirm scenario).
      // key must be the plain storage key (no s3:// prefix) for display via resolveRouterPath.
      const fileForSubmit: FileInfo | null =
        selectedFile ??
        (recoveredSourceFile
          ? {
              key: stripS3Prefix(recoveredSourceFile) || recoveredSourceFile,
              type: FileType.FILE,
              s3_uri: recoveredSourceFile,
            }
          : null);

      if (!fileForSubmit) {
        console.error("ValidationNavigation: No file selected at summary step");
        return;
      }

      // Extract file type from validation jobs
      const fileFormatJob = validationState?.jobs.find(
        (job) => job.title === "File Format Detection"
      );

      // Parse the file type in order to determine delimiter for ingest form
      const fileType = fileFormatJob?.result.match(/File type: (\w+)/)?.[1]?.toLowerCase() || "csv";
      const delimiter = fileType === "tsv" ? "\t" : ",";

      const validationMetadata: ValidationMetadata = {
        validatedFilePath: validationState?.validatedFilePath,
        summary: validationState?.summary,
        dataProfile: dataProfile,
        file_format: {
          type: "csv", //SNOWFLAKE only supports CSV/TSV currently, and tsv is handled via delimiter
          delimiter: delimiter,
          encoding: "UTF8",
          null_if: ["\\N", "NULL", ""],
          skip_header: 0, //The VALID_ROWS file will not have a header
        },
      };
      onConfirm?.();
      onFileValidated(fileForSubmit, validationMetadata);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      clearError();
    }
  };

  const canProceed = (): boolean => {
    if (currentStep === 0) {
      return canStartValidation && !isLoading && !isUploading;
    }
    if (currentStep === 1) {
      return !isUploading && isProfilingComplete;
    }
    return true;
  };

  const isStep0Busy = currentStep === 0 && isLoading;

  // User is starting fresh if they have an existing ingestion but selected a different file
  const isStartingFresh = hasIngestion && (!!pendingLocalFile || isSelectingDifferentFile());

  const getNextButtonLabel = (): string => {
    if (currentStep === 0) {
      if (isStartingFresh) {
        return "Start New Validation";
      }
      if (hasIngestion) {
        return "Continue to Validation";
      }
      return "Start Validation";
    }
    if (currentStep === 1) {
      return "Continue to Summary";
    }
    if (currentStep === 2) {
      if (validationState?.hasErrors) {
        return "Start Over";
      }
      return "Accept & Continue";
    }
    return "Next";
  };

  const getBackButtonLabel = (): string => {
    switch (currentStep) {
      case 1:
      case 2:
        return "Previous";
      default:
        return "Cancel";
    }
  };

  return (
    <div className="space-y-3">
      {/* Error display */}
      {error && (
        <div
          className={cn("p-3 rounded-lg text-sm border", "bg-red-50 border-red-200 text-red-700")}
        >
          {error}
        </div>
      )}

      {/* Info/warning banner for existing ingestion */}
      {hasIngestion && currentStep === 0 && (
        <div
          className={cn(
            "p-3 rounded-lg text-sm border",
            isStartingFresh
              ? "bg-yellow-50 border-yellow-300 text-yellow-800"
              : "bg-blue-50 border-blue-200 text-blue-700"
          )}
        >
          {isStartingFresh ? (
            <>
              <p className="font-medium">New file selected</p>
              <p className="text-xs mt-1">
                Starting a new validation will replace your existing in-progress validation.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">Existing validation found</p>
              <p className="text-xs mt-1">
                You have an in-progress validation. Click &ldquo;Continue to Validation&rdquo; to
                resume.
              </p>
            </>
          )}
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        {currentStep > 0 ? (
          <Button variant="outline" onClick={handleBack} disabled={isStep0Busy}>
            {getBackButtonLabel()}
          </Button>
        ) : (
          <Button variant="outline" onClick={onClose} disabled={isStep0Busy}>
            Cancel
          </Button>
        )}

        <Button onClick={handleNext} disabled={!canProceed()}>
          {isStep0Busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            getNextButtonLabel()
          )}
        </Button>
      </div>
    </div>
  );
};
